import { defaultState } from 'redux/modules/hourStates.constants';
import type { Hour } from 'types/hour';
import type { HoursDetailsFilter } from 'types/hours';
import type { EnrichedProject } from 'types/project';
import { naturalCompare } from 'utils/naturalSort';

function sortByBilled(
  a: Hour<EnrichedProject>,
  b: Hour<EnrichedProject>,
  direction: 'asc' | 'desc'
) {
  // If we don't have a .billed property, don't re-order
  // This is useful for /reports since we don't have billed property on client/project
  if (a.billed === undefined) return 0;

  const aRecord = `${a.billed}-${a.id}`;
  const bRecord = `${b.billed}-${b.id}`;

  if (direction === 'desc') {
    return naturalCompare(aRecord, bRecord);
  }
  return naturalCompare(bRecord, aRecord);
}

function sortByDay(a: Hour<EnrichedProject>, b: Hour<EnrichedProject>, direction: 'asc' | 'desc') {
  const aRecord = `${a.day}${a.id}`;
  const bRecord = `${b.day}${b.id}`;

  if (direction === 'asc') {
    return naturalCompare(aRecord, bRecord);
  }
  return naturalCompare(bRecord, aRecord);
}

export function sortReportableValues(
  a: Hour<EnrichedProject>,
  b: Hour<EnrichedProject>,
  field: string,
  direction: 'asc' | 'desc'
) {
  switch (field) {
    case 'name':
    case 'day':
      return sortByDay(a, b, direction);
    case 'billed':
      return sortByBilled(a, b, direction);
    case 'total_minutes':
      if (direction === 'asc') return a.duration.total_minutes - b.duration.total_minutes;
      if (direction === 'desc') return b.duration.total_minutes - a.duration.total_minutes;
      break;
    case 'estimated_total_minutes':
      if (direction === 'asc')
        return a.estimated_duration.total_minutes - b.estimated_duration.total_minutes;
      if (direction === 'desc')
        return b.estimated_duration.total_minutes - a.estimated_duration.total_minutes;
      break;
    case 'cost':
      if (direction === 'asc') return a.cost.amount - b.cost.amount;
      if (direction === 'desc') return b.cost.amount - a.cost.amount;
      break;
    case 'estimated_cost':
      if (direction === 'asc') return a.estimated_cost.amount - b.estimated_cost.amount;
      if (direction === 'desc') return b.estimated_cost.amount - a.estimated_cost.amount;
      break;
    case 'project':
      return (
        naturalCompare(a.project.base.name, b.project.base.name) * (direction === 'asc' ? 1 : -1)
      );
    case 'state':
      return (
        naturalCompare((a.state ?? defaultState).name, (b.state ?? defaultState).name) *
        (direction === 'asc' ? 1 : -1)
      );
    default:
      break;
  }

  return 1;
}

// Calculate how filters are set, used for setting "Clear filter" state
export function getFilterCount(filters: HoursDetailsFilter) {
  const filterToCheck = (Object.keys(filters) as Array<keyof HoursDetailsFilter>)
    .map(key => {
      const value = filters[key];
      if (key !== 'columns' && Array.isArray(value)) {
        return value.length;
      }
      return 0;
    })
    .filter(length => length > 0);

  if (filterToCheck.length) {
    return filterToCheck.reduce((filterLength, initialValue) => filterLength + initialValue);
  }
  return 0;
}

export const to2upto = (toDate: string) => {
  if (!toDate) return '';
  const upto = new Date(toDate);
  upto.setDate(upto.getDate() + 1);
  return upto.toISOString().split('T')[0];
};
